
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Trophy, ArrowRight, ExternalLink } from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

export interface NavItemProps {
  name: string;
  path: string;
  dropdown?: boolean;
  items?: { 
    name: string; 
    path: string; 
    description?: string; 
    icon?: React.ReactNode;
    external?: boolean;
  }[];
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  icon?: React.ReactNode;
}

interface NavItemsProps {
  navLinks: NavItemProps[];
  isScrolled?: boolean;
}

const NavItems = ({ navLinks, isScrolled }: NavItemsProps) => {
  const location = useLocation();

  return (
    <NavigationMenu className="z-10">
      <NavigationMenuList>
        {navLinks.map((link) => 
          link.dropdown ? (
            <NavigationMenuItem key={link.name}>
              <NavigationMenuTrigger
                className={cn(
                  "bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent hover:text-accent",
                  isScrolled ? "text-foreground/85" : "text-primary-foreground/90"
                )}
              >
                {link.name}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[500px] gap-2 p-4 md:w-[550px] md:grid-cols-2 lg:w-[600px]">
                  {link.items?.map((item) => (
                    <li key={item.name}>
                      <NavigationMenuLink asChild>
                        {item.external ? (
                          <a
                            href={item.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground group"
                            aria-label={`External link to ${item.name}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-medium leading-none">{item.name}</div>
                              <ExternalLink className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              {item.description}
                            </p>
                          </a>
                        ) : (
                          <Link
                            to={item.path}
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground group"
                            aria-label={`Navigate to ${item.name}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-medium leading-none">{item.name}</div>
                              <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              {item.description}
                            </p>
                          </Link>
                        )}
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
          ) : (
            <NavigationMenuItem key={link.path}>
              <Link
                to={link.path}
                className={cn(
                  "flex items-center px-4 py-2 text-sm font-medium transition-colors",
                  location.pathname === link.path
                    ? "text-accent"
                    : isScrolled
                      ? "text-foreground/85 hover:text-accent"
                      : "text-primary-foreground/90 hover:text-accent"
                )}
                aria-current={location.pathname === link.path ? "page" : undefined}
              >
                {link.icon && link.icon}
                {link.name}
              </Link>
            </NavigationMenuItem>
          )
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default NavItems;
